import { Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useRef } from 'react';

import { _getFile } from '@/api/files/queries';
import { _postFiles, FileMetadata } from '@/api/fund/mutations';
import UploadIcon from '@/assets/svg/ui-elements/upload-outline.svg';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import {
  Dropzone,
  FileItem,
  FileItemFrame,
  type FileUploadItem,
  Modal,
  Tooltip,
  Typography,
} from '@/shared/design-system';
import { useProfileSlice } from '@/stores/selectors';
import { uploadToS3 } from '@/utils/upload-to-s3';

export type HeadShotModalProps = {
  userProfileId: string;
};

export const HeadshotModal = () => {
  const { open, handleClose, props } = useGlobalModal('HEADSHOT_MODAL');
  const inputRef = useRef<HTMLInputElement>(null);
  const { form, setForm } = useProfileSlice();

  const { data: currentFile, isLoading } = useQuery({
    ..._getFile(form.headshotFileId as string),
    enabled: !!form.headshotFileId,
  });

  const pickFile = () => inputRef.current?.click();

  const {
    mutate,
    isPending: isUploading,
    variables: uploadingFile,
  } = useMutation({
    mutationFn: _postFiles,
    onSuccess: async (data, variables) => {
      await uploadToS3({
        uploadUrl: data.uploadUrl,
        uploadHeaders: data.uploadHeaders as Record<string, string>,
        file: variables.file,
        fileId: data.fileId,
      });
      setForm({ headshotFileId: data.fileId });
    },
  });

  const processFile = (file: File | null) => {
    if (!file) return;
    const metadata = {
      filename: file.name,
      contentType: file.type,
      category: 'profile',
      ownerType: 'user',
      ownerId: props?.userProfileId,
      file,
    } as FileMetadata & { file: File };
    mutate(metadata);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    processFile(file ?? null);
    e.currentTarget.value = '';
  };

  const handleRemove = () => {
    setForm({ headshotFileId: undefined as unknown as string });
    if (inputRef.current) inputRef.current.value = '';
  };

  const loading = isUploading || isLoading;
  const hasImage = !!form.headshotFileId && currentFile;

  const getHeadshotItem = (): FileUploadItem | null => {
    if (isUploading) {
      return {
        id: 'uploading-headshot',
        name: uploadingFile?.file.name ?? 'Headshot',
        status: 'uploading',
        progress: null,
      };
    }
    if (!form.headshotFileId || !currentFile) return null;
    return {
      id: currentFile.id,
      name: currentFile.fileName,
      size: currentFile.size,
      contentType: currentFile.contentType,
      previewUrl: currentFile.url,
      status: 'uploaded',
    };
  };
  const headshotItem = getHeadshotItem();

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
      }}
      maxWidth={504}
      title="Upload headshot"
      description="A picture helps people recognize you and lets you know when you're signed in to your account"
      secondaryAction={
        hasImage
          ? {
              label: 'Re-upload',
              color: 'tertiary',
              variant: 'outline',
              onClick: pickFile,
              disabled: loading,
            }
          : undefined
      }
      primaryAction={
        hasImage ? { label: 'Save', onClick: handleClose, disabled: loading } : undefined
      }
    >
      {headshotItem ? (
        <FileItemFrame>
          <FileItem
            variant="image"
            file={headshotItem}
            onRemove={loading ? undefined : handleRemove}
          />
        </FileItemFrame>
      ) : (
        <Dropzone
          accept="image/jpeg,image/png"
          subject="image"
          hint="JPG, PNG • Max 2MB"
          disabled={loading}
          onFilesSelected={([file]) => processFile(file ?? null)}
          icon={
            <Tooltip
              side="top"
              title={
                <Stack flexDirection="column" gap="2px">
                  <Typography variant="body3" fontWeight={600}>
                    Image guidelines:
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '16px' }}>
                    <li>Aspect ratio: 1:1</li>
                    <li>Minimum size: 256 × 256 px</li>
                    <li>Recommended size: 512 × 512 px</li>
                    <li>Formats: PNG</li>
                    <li>Maximum file size: 2 MB</li>
                  </ul>
                </Stack>
              }
            >
              <span>
                <UploadIcon />
              </span>
            </Tooltip>
          }
        />
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept="image/jpeg,image/png"
        onChange={handleChange}
      />
    </Modal>
  );
};
